import { BsLayoutThreeColumns } from 'react-icons/bs';
import styles from './ColumnsOptionsSidebar.module.scss';
import { useTranslation } from 'react-i18next';
import { FaEye } from 'react-icons/fa';
import { RiFileExcel2Line, RiRefreshLine } from 'react-icons/ri';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { IoMoveOutline } from 'react-icons/io5';
import { SpinnerCircular } from 'spinners-react';
import { StrictModeDroppable } from 'common/components/strictModeDroppable/StrictModeDroppable';

export function columnsReorder<TRow>(list: ColumnsOptionsSidebarColumn<TRow>[], startIndex: number, endIndex: number) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
}

export interface ColumnsOptionsSidebarColumn<TRow> {
    field: TRow;
    name: string;
}

export interface ColumnsOptionsSidebarColumnOrder<TRow> {
    i: number;
    field: TRow;
}

interface Props<TRow> {
    columns: ColumnsOptionsSidebarColumn<TRow>[];
    hiddenColumns: TRow[];
    canDrag?: boolean;
    toggleTopOffset?: number;  // Offset from sidebar top to align with table header
    onChangeHiddenColumns: (hiddenColumns: TRow[]) => void;
    onChangeColumnsOrder: (columnsOrder: ColumnsOptionsSidebarColumnOrder<TRow>[]) => void;
    onExportExcel?: () => void;
    isExporting?: boolean;
    onReset?: () => void;
}

const ColumnsOptionsSidebar = <TRow extends any>({
    columns,
    hiddenColumns,
    canDrag = true,
    toggleTopOffset = 0,
    onChangeHiddenColumns,
    onChangeColumnsOrder,
    onExportExcel,
    isExporting = false,
    onReset,
}: Props<TRow>) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const onClickItem = (col: ColumnsOptionsSidebarColumn<TRow>) => {
        const isCurrentlyHidden = hiddenColumns.find(c => c === col.field);

        // Se está a tentar esconder e é a última coluna visível, não permitir
        if (!isCurrentlyHidden) {
            const visibleCount = columns.length - hiddenColumns.length;
            if (visibleCount <= 1) {
                return;
            }
        }

        if (isCurrentlyHidden) {
            onChangeHiddenColumns(hiddenColumns.filter(c => c !== col.field));
        } else {
            onChangeHiddenColumns([...hiddenColumns, col.field]);
        }
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const items = columnsReorder(
            [...columns],
            result.source.index,
            result.destination.index
        );
        onChangeColumnsOrder(items.map((col, i) => ({ i, field: col.field })));
    };

    const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
        userSelect: 'none' as const,
        padding: '0.25rem',
        ...draggableStyle
    });

    const sidebarContent = (
        <div className={`${styles.sidebar} ${isOpen && styles.isOpen}`}>
            <div
                className={styles.toggle}
                onClick={() => setIsOpen(o => !o)}
                style={{ top: toggleTopOffset }}
            >
                <BsLayoutThreeColumns />
            </div>
            <div className={styles.content}>
                <div className={styles.title}>
                    <span>{t('common.visible_columns')}</span>
                    <div className={styles.titleButtons}>
                        {onReset && (
                            <button
                                className={styles.exportButton}
                                onClick={onReset}
                                title={t('common.reset_columns') as string}
                            >
                                <RiRefreshLine />
                            </button>
                        )}
                        {onExportExcel && (
                            <button
                                className={styles.exportButton}
                                onClick={onExportExcel}
                                disabled={isExporting}
                                title={t('common.export_excel') as string}
                            >
                                {isExporting
                                    ? <SpinnerCircular size={18} speed={150} secondaryColor="rgba(0,0,0,0.20)" color="#034e92" />
                                    : <RiFileExcel2Line />}
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.list}>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <StrictModeDroppable droppableId="droppable" isDropDisabled={!canDrag}>
                            {provided => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {columns.map((col, index) => {
                                        const isHidden = hiddenColumns.find(c => c === col.field);
                                        const visibleCount = columns.length - hiddenColumns.length;
                                        const isLastVisible = !isHidden && visibleCount === 1;
                                        return (
                                            <Draggable
                                                key={col.field as string}
                                                draggableId={col.field as string}
                                                index={index}
                                                isDragDisabled={!canDrag}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style
                                                        )}
                                                    >
                                                        <div
                                                            key={col.field as string}
                                                            className={`${styles.listItem} ${(!isHidden && styles.listItemActive)} ${(isLastVisible && styles.listItemDisabled)}`}
                                                            onClick={() => onClickItem(col)}
                                                            style={isLastVisible ? { cursor: 'not-allowed' } : undefined}
                                                        >
                                                            <span className={styles.itemIcon}>
                                                                <FaEye />
                                                            </span>
                                                            <span className={styles.itemText}>{col.name}</span>
                                                            {canDrag && (
                                                                <span
                                                                    {...provided.dragHandleProps}
                                                                    className={styles.columnConfigCursor}
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <IoMoveOutline />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                </div>
            </div>
        </div>
    );

    // Use portal to render at body level
    return createPortal(sidebarContent, document.body);
};

export default ColumnsOptionsSidebar;
